import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import { Platform, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useBankAccount } from '@/api/partners/bank-account/use-bank-account';
import { useVerifyBankAccount } from '@/api/partners/bank-account/use-verify-bank-account';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { isDesktop } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { Button, Input, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { CreditCard } from '@/ui/icons/credit-card';

const steps = [
  'We deposit two amounts under $1 via ACH',
  'They land in 1-3 business days',
  'Come back and confirm the amounts',
];

function toCents(value: string): number | null {
  const normalized = value.replace(/[^0-9.]/g, '');
  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  if (Number.isNaN(parsed)) {
    return null;
  }

  return Math.round(parsed * 100);
}

export default function VerifyBankAccount() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data: bankAccount } = useBankAccount();
  const {
    mutateAsync: verifyBankAccount,
    isPending,
    error,
  } = useVerifyBankAccount();

  const [step, setStep] = useState<'intro' | 'confirm'>('intro');
  const [amount1, setAmount1] = useState('');
  const [amount2, setAmount2] = useState('');

  const last4 = bankAccount?.last4 ?? '';

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Partner Bank Account Verify',
    });
  }, []);

  const canVerify = useMemo(() => {
    return toCents(amount1) !== null && toCents(amount2) !== null;
  }, [amount1, amount2]);

  const handleVerify = async () => {
    const amount1Cents = toCents(amount1);
    const amount2Cents = toCents(amount2);

    if (amount1Cents === null || amount2Cents === null) {
      return;
    }

    try {
      await verifyBankAccount({ amount1Cents, amount2Cents });

      await queryClient.invalidateQueries({
        queryKey: ['partner/bank-account'],
      });

      Toast.show({
        text1: 'Bank account verified',
        type: 'success',
      });

      router.replace('/partner/more/bank-account');
    } catch (e) {
      console.error('Error verifying bank account:', e);
    }
  };

  const desktopNav =
    Platform.OS === 'web' && isDesktop() ? (
      <View style={styles.desktopNav}>
        <NavigationButton
          type={NavigationButtonTypes.back_with_text}
          onPress={() => {
            if (step === 'confirm') {
              setStep('intro');
              return;
            }
            router.navigate('/partner/more/bank-account');
          }}
        />
      </View>
    ) : null;

  const accountRow = (
    <View style={styles.accountRow}>
      <Icon
        type="dressed"
        icon={<CreditCard />}
        fillColor={theme.colors.icon.color}
        width={18}
        height={18}
      />

      <Text type="bodyRegular">Account ending {`••${last4}`}</Text>
    </View>
  );

  if (step === 'intro') {
    return (
      <View style={styles.container}>
        <View style={styles.body}>
          {desktopNav}

          <View style={{ gap: 8 }}>
            <Text type="title2">Check Your Account In 1-3 Days</Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              We sent two small deposits to verify you own this account.
            </Text>
          </View>

          <View style={{ gap: 16 }}>
            {steps.map((text, index) => (
              <View key={text} style={styles.stepRow}>
                <View style={styles.stepBadge}>
                  <Text
                    type="caption1"
                    style={{ color: theme.colors.progressBar.text }}
                  >
                    {index + 1}
                  </Text>
                </View>

                <Text type="bodyRegular" style={{ flexShrink: 1 }}>
                  {text}
                </Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.footer}>
          {accountRow}

          <Button
            text="Enter Amounts"
            type="primary"
            onPress={() => setStep('confirm')}
            style={styles.enterAmountsButton}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <KeyboardAwareScrollView
        style={styles.scrollView}
        contentContainerStyle={{ paddingBottom: 100 }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.body}>
          {desktopNav}

          <View style={{ gap: 8 }}>
            <Text type="title2">Confirm Your Deposits</Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              Enter the two amounts we sent to your account.
            </Text>
          </View>

          {accountRow}

          <View style={{ gap: 8 }}>
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              Deposit amounts
            </Text>

            <View style={{ flexDirection: 'row', gap: 16 }}>
              <Input
                label=""
                placeholder="$0.00"
                keyboardType="decimal-pad"
                value={amount1}
                onChangeText={setAmount1}
                containerStyle={{ flex: 1 }}
              />

              <Input
                label=""
                placeholder="$0.00"
                keyboardType="decimal-pad"
                value={amount2}
                onChangeText={setAmount2}
                containerStyle={{ flex: 1 }}
              />
            </View>

            {error ? (
              <Text type="caption1" style={{ color: theme.colors.error }}>
                {error.message}
              </Text>
            ) : (
              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                Find them in your bank's transaction history.
              </Text>
            )}
          </View>

          <View style={styles.buttonDesktop}>
            <Button
              text="Verify Amount"
              type="primary"
              onPress={handleVerify}
              loading={isPending}
              disabled={!canVerify || isPending}
              style={{ width: 160 }}
            />
          </View>
        </View>
      </KeyboardAwareScrollView>

      <View style={styles.buttonMobile}>
        <Button
          text="Verify Amount"
          type="primary"
          onPress={handleVerify}
          loading={isPending}
          disabled={!canVerify || isPending}
        />
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
  },
  scrollView: {
    flexGrow: 1,
    paddingHorizontal: {
      md: 56,
    },
  },
  body: {
    flexGrow: {
      xs: 1,
      md: 0,
    },
    alignSelf: 'center',
    width: {
      xs: '100%',
      md: 600,
    },
    paddingHorizontal: {
      xs: 16,
      md: 0,
    },
    paddingTop: {
      xs: 16,
      md: 56,
    },
    gap: 32,
  },
  desktopNav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
  },
  stepRow: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
  },
  stepBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.progressBar.active.backgroundColor,
  },
  accountRow: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
  },
  footer: {
    padding: 16,
    gap: 16,
    alignSelf: 'center',
    width: {
      xs: '100%',
      md: 600,
    },
    flexDirection: {
      md: 'row',
    },
    justifyContent: {
      md: 'flex-end',
    },
  },
  enterAmountsButton: {
    width: {
      md: 175,
    },
  },
  buttonMobile: {
    display: {
      xs: 'flex',
      md: 'none',
    },
    padding: 16,
  },
  buttonDesktop: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    alignItems: {
      md: 'flex-end',
    },
  },
}));
